/**
 * Memoized Intl.DateTimeFormat instance for high-performance timestamp formatting.
 * Creating Intl.DateTimeFormat objects is expensive; reusing a single instance
 * significantly reduces overhead in render loops.
 */
const timeFormatter = new Intl.DateTimeFormat("en-US", {
	hour12: false,
	hour: "2-digit",
	minute: "2-digit",
	second: "2-digit",
	timeZone: "Asia/Tokyo",
});

/**
 * Japanese date formatters for history and detail views.
 */
const shortJaDateFormatter = new Intl.DateTimeFormat("ja-JP", {
	month: "narrow",
	day: "numeric",
	hour: "2-digit",
	minute: "2-digit",
});

const fullJaDateFormatter = new Intl.DateTimeFormat("ja-JP");

/**
 * Formats a Date object or timestamp into a HH:mm:ss string.
 * @param date The date to format
 * @returns Formatted time string
 */
export const formatTime = (date: Date | number | string): string => {
	// Performance optimization: Avoid new Date() allocation if input is already Date or number.
	// Intl.DateTimeFormat.format() supports both Date and number natively.
	if (date instanceof Date || typeof date === "number") {
		return timeFormatter.format(date);
	}
	return timeFormatter.format(new Date(date));
};

/**
 * Formats a Date object or timestamp into a Japanese short date string.
 * Used in History list.
 * @param date The date to format
 * @returns Formatted date string
 */
export const formatShortJapaneseDate = (
	date: Date | number | string,
): string => {
	// Performance optimization: Bypass redundant new Date() calls for Date/number types.
	if (date instanceof Date || typeof date === "number") {
		return shortJaDateFormatter.format(date);
	}
	return shortJaDateFormatter.format(new Date(date));
};

/**
 * Formats a Date object or timestamp into a full Japanese date string.
 * Used in Session Detail view.
 * @param date The date to format
 * @returns Formatted date string
 */
export const formatFullJapaneseDate = (
	date: Date | number | string,
): string => {
	// Performance optimization: Bypass redundant new Date() calls for Date/number types.
	if (date instanceof Date || typeof date === "number") {
		return fullJaDateFormatter.format(date);
	}
	return fullJaDateFormatter.format(new Date(date));
};
